/**
 * The roster SHOWS a person as "<English first name> <initial>.", e.g. `А.Долгормаа` shows `Dolgormaa A.`.
 *
 * The label is built ONCE, on the server (`services/roster_labels.py`), over
 * the whole roster -- fallbacks and collisions included -- and served as
 * `label` beside `name`. These are the client's only rules about it:
 *
 *   - `name` stays the KEY: grids, filing, bus/hse/perf/exp and saves join on
 *     it, so nothing here changes what is stored or posted;
 *   - an absent or blank label (an old server, a name with no roster row)
 *     shows the key, never an empty cell;
 *   - a search matches what is SHOWN as well as the key, so typing "Dolg"
 *     finds the row labelled "Dolgormaa A.".
 */
import { nameMatches } from './names'
import { STACK_SEP, stackOf } from './derive'

export type RosterLabels = ReadonlyMap<string, string>

/** `{key -> label}` from anything carrying `name` and an optional `label`. */
export function labelMap(
  people: ReadonlyArray<{ name: string; label?: string | null }> | null | undefined,
): RosterLabels {
  const out = new Map<string, string>()
  for (const p of people ?? []) {
    const label = (p.label ?? '').trim()
    if (p.name && label) out.set(p.name, label)
  }
  return out
}

/** What to show for `name`: its label, else the key itself. */
export function labelOf(labels: RosterLabels | null | undefined, name: string): string {
  return labels?.get(name) || name
}

/**
 * Does the person keyed `name` match `query`? The key's own rule
 * (`nameMatches`), OR the shown label: one letter matches the label's first
 * letter, two or more anywhere in it.
 */
export function labelMatches(
  labels: RosterLabels | null | undefined,
  name: string,
  query: string,
): boolean {
  const q = String(query ?? '').trim().toLowerCase()
  if (!q) return true
  if (nameMatches(name, q)) return true
  const shown = labels?.get(name)?.toLowerCase()
  if (!shown) return false
  return q.length === 1 ? shown.startsWith(q) : shown.includes(q)
}

/** What to show for a roster person object: its `label`, else its key. */
export function personLabel(person: { name: string; label?: string | null }): string {
  return (person.label ?? '').trim() || person.name
}

/**
 * A planned code is SHOWN with a `p` in front (ЭТТ plan `pd/pn/pl`,
 * actual `d/n/l`).
 *
 * ⛔ DISPLAY ONLY. The stored code stays `d/n/l` in both grids, so the
 * plan-vs-actual compare, the clipboard, the xlsx export and every save carry
 * the bare code. Only a WORK code with a project takes the prefix: travel `T`,
 * downtime `DT` and leave `al` are not shifts on a project and read the same in
 * both grids.
 */
const PLAN_PREFIX = 'p'

interface CodeLike {
  code: string
  project: string
  cat: string
}

function prefixed(code: string, codes: ReadonlyMap<string, CodeLike> | undefined): boolean {
  const known = codes?.get(code)
  return Boolean(known && known.cat === 'work' && known.project !== '')
}

/** What a code SHOWS as: `p`+code for a planned project shift, else the code. */
export function displayCode(
  code: string,
  plan: boolean,
  codes: ReadonlyMap<string, CodeLike> | undefined,
): string {
  return plan && prefixed(code, codes) ? PLAN_PREFIX + code : code
}

/**
 * The stored code behind something typed or pasted. A known code passes
 * through; `pd` maps back to `d` when `d` is a prefixed code and `pd` is not
 * itself a code -- so a block copied out of a spreadsheet that shows the plan
 * letters still pastes. Anything else is returned unchanged for the caller's
 * unknown-code check to name.
 */
export function storedCode(text: string, codes: ReadonlyMap<string, CodeLike> | undefined): string {
  if (codes?.has(text)) return text
  if (text.startsWith(PLAN_PREFIX)) {
    const bare = text.slice(PLAN_PREFIX.length)
    if (prefixed(bare, codes)) return bare
  }
  return text
}

/** A plan cell as the grid writes it: each code of a stack, a project code with its `p`. */
export function planText(value: string | null | undefined, codes: Parameters<typeof displayCode>[2]): string {
  return stackOf(value ?? '')
    .map((c) => displayCode(c, true, codes))
    .join(STACK_SEP)
}
